import React, { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Play, Pause, Search, Volume2, X, Check } from "lucide-react";
import { cn } from "@/lib/utils";

interface VoiceOption {
  id: string;
  name: string;
  gender: 'male' | 'female' | 'neutral';
  preview_url?: string;
}

interface VoiceSelectionModalProps {
  voices: VoiceOption[];
  selectedVoiceId?: string;
  onVoiceSelect: (voiceId: string) => void;
  triggerElement?: React.ReactNode;
  characterName?: string;
}

export function VoiceSelectionModal({
  voices,
  selectedVoiceId,
  onVoiceSelect,
  triggerElement,
  characterName
}: VoiceSelectionModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedGender, setSelectedGender] = useState<string>("all");
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);
  const [tempSelectedVoiceId, setTempSelectedVoiceId] = useState(selectedVoiceId || "");
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Reset temp selection when modal opens
  useEffect(() => {
    if (isOpen) {
      setTempSelectedVoiceId(selectedVoiceId || "");
      setSearchTerm("");
      setSelectedGender("all");
    }
  }, [isOpen, selectedVoiceId]);

  // Clean up audio when component unmounts
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const handlePlayPreview = async (voice: VoiceOption) => {
    if (!voice.preview_url) return;

    // Stop current audio if playing
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }

    if (playingVoiceId === voice.id) {
      setPlayingVoiceId(null);
      return;
    }

    try {
      setPlayingVoiceId(voice.id);
      audioRef.current = new Audio(voice.preview_url);
      
      audioRef.current.onended = () => {
        setPlayingVoiceId(null);
        audioRef.current = null;
      };

      audioRef.current.onerror = () => {
        setPlayingVoiceId(null);
        audioRef.current = null;
        console.error(`Failed to play preview for voice: ${voice.name}`);
      };

      audioRef.current.onloadstart = () => {
        // Audio is starting to load
      };

      await audioRef.current.play();
    } catch (error) {
      setPlayingVoiceId(null);
      audioRef.current = null;
      console.error(`Error playing voice preview: ${error}`);
    }
  };

  const handleVoiceSelection = (voiceId: string) => {
    setTempSelectedVoiceId(voiceId);
    // Auto-confirm and close modal immediately
    onVoiceSelect(voiceId);
    setIsOpen(false);
  };

  const handleConfirmSelection = () => {
    if (tempSelectedVoiceId) {
      onVoiceSelect(tempSelectedVoiceId);
    }
    setIsOpen(false);
  };

  const handleCancel = () => {
    setTempSelectedVoiceId(selectedVoiceId || "");
    setIsOpen(false);
  };

  // Filter voices based on search and gender
  const filteredVoices = voices.filter(voice => {
    const matchesSearch = voice.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesGender = selectedGender === "all" || voice.gender === selectedGender;
    return matchesSearch && matchesGender;
  });

  // Get gender counts
  const genderCounts = {
    all: voices.length,
    male: voices.filter(v => v.gender === 'male').length,
    female: voices.filter(v => v.gender === 'female').length,
    neutral: voices.filter(v => v.gender === 'neutral').length
  };

  // Get selected voice for display
  const selectedVoice = voices.find(v => v.id === selectedVoiceId);

  const defaultTrigger = (
    <Button variant="outline" className="w-full justify-between">
      <span className="truncate">
        {selectedVoice ? `${selectedVoice.name} (${selectedVoice.gender})` : "Select a voice"}
      </span>
      <Volume2 className="h-4 w-4 ml-2 flex-shrink-0" />
    </Button>
  );

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {triggerElement || defaultTrigger}
      </DialogTrigger>
      <DialogContent className="max-w-4xl w-[90vw] h-[85vh] flex flex-col p-0">
        <DialogHeader className="px-6 py-4 border-b">
          <DialogTitle className="flex items-center gap-2">
            <Volume2 className="h-5 w-5" />
            Select Voice {characterName && `for ${characterName}`}
          </DialogTitle>
          <DialogDescription>
            Browse and preview voices to find the perfect match. Click on any voice card to select it.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 flex flex-col min-h-0">
          {/* Search and Filter Controls */}
          <div className="px-6 py-4 border-b space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search voices..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            <div className="flex gap-2 flex-wrap">
              {(['all', 'male', 'female', 'neutral'] as const).map((gender) => (
                <Button
                  key={gender}
                  variant={selectedGender === gender ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedGender(gender)}
                  className="capitalize"
                >
                  {gender} ({genderCounts[gender]})
                </Button>
              ))}
            </div>
          </div>

          {/* Voice Grid */}
          <ScrollArea className="flex-1 px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 py-4">
              {filteredVoices.map((voice) => (
                <Card
                  key={voice.id}
                  className={cn(
                    "cursor-pointer transition-all hover:shadow-md border-2 relative",
                    tempSelectedVoiceId === voice.id
                      ? "border-primary ring-2 ring-primary/20 bg-primary/5"
                      : "border-border hover:border-primary/50"
                  )}
                  onClick={() => handleVoiceSelection(voice.id)}
                >
                  <CardContent className="p-3">
                    <div className="flex flex-col space-y-2">
                      <div className="flex items-start justify-between">
                        <h3 className="font-medium truncate text-sm leading-tight">{voice.name}</h3>
                        {tempSelectedVoiceId === voice.id && (
                          <Check className="h-4 w-4 text-primary flex-shrink-0 ml-2" />
                        )}
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <Badge variant="secondary" className="text-xs capitalize">
                          {voice.gender}
                        </Badge>
                        
                        {voice.preview_url && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePlayPreview(voice);
                            }}
                            className={cn(
                              "h-7 w-7 p-0 rounded-full",
                              playingVoiceId === voice.id 
                                ? "bg-primary text-primary-foreground hover:bg-primary/90" 
                                : "hover:bg-primary/10"
                            )}
                            title="Preview voice"
                          >
                            {playingVoiceId === voice.id ? (
                              <Pause className="h-3 w-3" />
                            ) : (
                              <Play className="h-3 w-3" />
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {filteredVoices.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                <Volume2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No voices found matching your criteria.</p>
                <p className="text-sm mt-1">Try adjusting your search or filter options.</p>
              </div>
            )}
          </ScrollArea>

          {/* Action Buttons */}
          <div className="px-6 py-4 border-t flex justify-end gap-3">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmSelection}
              disabled={!tempSelectedVoiceId}
            >
              Select Voice
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}